<template>
  <li class="tabs-title" :class="{ 'is-active': active }" role="presentation">
    <a
      role="tab"
      :tabindex="active ? 0 : -1"
      :aria-selected="active ? 'true' : 'false'"
      :aria-disabled="disabled ? 'true' : 'false'"
      @click="onTabClick"
      @keydown.enter.prevent="onTabClick"
      @keydown.space.prevent="onTabClick"
      @keydown.left.prevent="onArrowKey(-1)"
      @keydown.right.prevent="onArrowKey(1)"
      @keydown.home.prevent="onEdgeKey(0)"
      @keydown.end.prevent="onEdgeKey(-1)"
    >
      {{ name }}
      <div v-if="showBadge" class="badge">
        <span>
          {{ getItemCount }}
        </span>
      </div>
    </a>
  </li>
</template>
<script>
export default {
  name: 'WootTabsItem',
  props: {
    index: {
      type: Number,
      default: 0,
    },
    name: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    count: {
      type: Number,
      default: 0,
    },
    showBadge: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    active() {
      return this.index === this.$parent.index;
    },

    getItemCount() {
      return this.count;
    },
  },

  methods: {
    onTabClick(event) {
      event.preventDefault();
      if (!this.disabled) {
        this.$parent.$emit('change', this.index);
      }
    },

    // Every tab in the strip. Only one of them is in the tab order at a time
    // (roving tabindex), so the arrow keys are the way to reach the rest.
    siblingTabs() {
      const list = this.$el.parentElement;
      return list ? Array.from(list.querySelectorAll('[role="tab"]')) : [];
    },

    focusTabAt(position) {
      const tabs = this.siblingTabs();
      if (!tabs.length) return;
      const target =
        position < 0 ? tabs[tabs.length + position] : tabs[position];
      if (target) target.focus();
    },

    // Arabic reads the other way, so the right arrow has to walk backwards.
    onArrowKey(step) {
      const tabs = this.siblingTabs();
      if (!tabs.length) return;
      const isRTL = getComputedStyle(this.$el).direction === 'rtl';
      const delta = isRTL ? -step : step;
      const current = tabs.indexOf(this.$el.querySelector('[role="tab"]'));
      const next = (current + delta + tabs.length) % tabs.length;
      tabs[next].focus();
    },

    onEdgeKey(position) {
      this.focusTabAt(position);
    },
  },
};
</script>
<style lang="scss" scoped>
.tabs-title {
  a {
    // The underline is the tab's whole identity, so give it something to animate
    // from and keep the label from jumping when it appears.
    @apply cursor-pointer transition-colors duration-150 ease-out;

    &:focus-visible {
      @apply outline-none ring-2 ring-accent-mint/70 ring-offset-2 ring-offset-white dark:ring-offset-ink rounded-sm;
    }

    &:focus:not(:focus-visible) {
      @apply outline-none;
    }
  }

  // Accent as TEXT and BORDER is `accent-mint`, never the `woot` ramp — on the ink
  // ground `text-accent-mint` measures 3.40:1 against `accent-mint`'s 13.76:1. Light
  // mode is unchanged: both resolve to #087a54 there.
  &.is-active a {
    @apply text-accent-mint border-accent-mint;
  }

  &.is-active .badge {
    @apply text-accent-mint;
  }

  &[aria-disabled='true'] a,
  a[aria-disabled='true'] {
    @apply cursor-not-allowed opacity-50;
  }

  @media (prefers-reduced-motion: reduce) {
    a {
      @apply transition-none;
    }
  }
}
</style>
